// Install necessary packages before running:
// npm install @mui/material @emotion/react @emotion/styled formik

import React, {useState} from "react";
import { Button, TextField, Box, Typography, Link, Select, MenuItem, FormControl, InputLabel, Grid, Alert, AlertTitle, NativeSelect, useTheme } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { setRole } from "../Reducers/role";
import { useDispatch } from "react-redux";
import ErrorAlert from "../Components/Alerts/ErrorAlert";
import { useNavigate } from "react-router-dom";
import { AWS_LINK, DEV_LINK } from "../Static/static";


// Validation Schema for Formik
const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email format").required("Email is required"),
  password: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
});

export default function Login() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();

    const [loginType, setLoginType] = useState("ROOT");
    const [errorTrigger, setErrorTrigger] = useState(false);
    const [error, setError] = useState({title: null, message: null});
    
    const handleSubmit = async (values) => {
        let payload = {...values, type: loginType}
        let json_data = JSON.stringify(payload);
        try {
            // March 25 switched from token: response.data.token,
            const LINK = process.env.NODE_ENV === "production" ? AWS_LINK : DEV_LINK;
            let response = await axios.post(LINK + "/login", json_data, {withCredentials: true});
            if (response.status === 200) {
                // Session storage
                dispatch(setRole({
                        role: loginType, 
                        id: response.data.user.id,
                        permissions: response.data.user.permissions,
                        organization_id: response.data.user.organization_id,
                        email: response.data.user.email, 
                        token: response.data.token,                        
                        tutor_locations: response.data.tutor_locations,
                        tutor_programs: response.data.tutor_programs,
                        first_name: response.data.user.first_name,
                        last_name: response.data.user.last_name
                    }))
                // Long duration token 5 + hours
                sessionStorage.setItem('token', response.data.token);
                localStorage.setItem('theme', 'light');
                navigate('/dashboard');
                return;
            } else {
                setError({title: 'Error', message: 'Unable to sign in. try again.'})
                setErrorTrigger(true);
                return;
            }
        }
        catch (error) {
            setError({title: 'Error', message: error.response.data})
            setErrorTrigger(true);
            return;
        }
    };

    const closeErrorTrigger = () => {
        setErrorTrigger(false);
        setError({title: null, message: null})
    }


    return (
        <Box
          sx={{
            width: "100%",
            maxWidth: 450,
            margin: "auto",
            mt: 8,
            p: 4,
            boxShadow: theme.palette.mode === 'light'
              ? '0px 4px 20px rgba(0, 0, 0, 0.1)'
              : '0px 4px 20px rgba(255, 255, 255, 0.05)',
            borderRadius: 1,
            position: 'relative',
            bgcolor: 'background.paper',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              bgcolor: 'primary.light',
              borderTopLeftRadius: 5,
              borderTopRightRadius: 5
            }
          }}
        >
  <Box textAlign="center" mb={4}>
    <Typography 
      fontWeight={700} 
      variant="h4" 
      sx={{ 
        color: theme.palette.secondary,
        letterSpacing: '0.5px',
        mb: 1
      }}
    >
      Welcome Back
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Please enter your credentials to login
    </Typography>
  </Box>

  <ErrorAlert open={errorTrigger} onClose={closeErrorTrigger} title={error.title} message={error.message} />

  <FormControl fullWidth sx={{ mb: 3 }}>
    <InputLabel id="login-type-label" sx={{ color: 'text.secondary' }}>User type</InputLabel>
    <NativeSelect
      labelId="login-type-label"
      value={loginType}
      onChange={(e) => setLoginType(e.target.value)}
      sx={{
        '& select': {
          py: 1.5,
          borderRadius: 1,
          backgroundColor: 'background.default',
          color: 'text.primary'
        }
      }}
    >
      <option value="ROOT">Root</option>
      <option value="ADMIN">Staff</option>
      <option value="TUTOR">Tutor</option>
    </NativeSelect>
  </FormControl>

  <Formik
    initialValues={{ email: "", password: "" }}
    validationSchema={LoginSchema}
    onSubmit={handleSubmit}
  >
    {({ errors, touched }) => (
      <Form>
        <Box mb={3}>
          <Field
            as={TextField}
            name="email"
            label="Email"
            fullWidth
            variant="outlined"
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email && errors.email}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              }
            }}
          />
        </Box>
        <Box mb={3}>
          <Field
            as={TextField}
            name="password"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            error={touched.password && Boolean(errors.password)}
            helperText={touched.password && errors.password}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              }
            }}
          />
        </Box>
        <Button 
          type="submit" 
          variant="contained"
          color="secondary" 
          fullWidth 
          sx={{ 
            mb: 2,
            py: 1.5,
            borderRadius: 1,
            
          }}
        >
          Login
        </Button>
      </Form>
    )}
  </Formik>

  <Grid container justifyContent="space-between" mt={3}>
    <Link 
      href="/forgot_password" 
      underline="none" 
      variant="body2"
      sx={{
        color: 'text.secondary',
        '&:hover': {
          color: 'primary.main',
          textDecoration: 'underline'
        }
      }}
    >
      Forgot Password?
    </Link>
    <Link 
      href="/register" 
      underline="none" 
      variant="body2"
      sx={{
        color: 'text.secondary',
        '&:hover': {
          color: 'primary.main',
          textDecoration: 'underline'
        }
      }}
    >
      Create an account
    </Link>
  </Grid>
</Box>
    );
};

